import * as React from 'react'
import { Nav } from './nav'
import { OrgSkillsTable } from './table'
import { TableProvider } from './table/context/table'
import { useRailsContext } from 'components/rails-context'
import { deserialize } from '../../api/utils/deserialize'
import { OrgSkillsTableRow } from './table/data'
import { Skill } from 'store/modules/skills'
import { store } from 'store/index'
import { observer } from 'mobx-react-lite'
import { parseISO } from 'date-fns'
import { fetchJson, reportLoadFailure } from 'app/packs/src/utils/fetch-json'
import { LoadFailed } from 'components/load-failed'
import { IJsonPatch, onPatch } from 'mobx-state-tree'
import {
  DeletedSkillContext,
  RemoveSkillContext,
  BulkDeletedSkillContext,
} from './deleted-skill-context'
import { SkillInsightsDrawer } from './side-panel'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { has } from 'mobx'

const patchRegex = /\/(?<module>.+?)\/data\/(?<id>.+?)\//

export const OrgSkillsPage = observer(() => {
  const [allLoading, setAllLoading] = React.useState(true)
  const [failed, setFailed] = React.useState(false)
  const [data, setData] = React.useState<OrgSkillsTableRow[] | null>(null)
  const [checkedSkillIds, setCheckedSkillIds] = React.useState<string[]>([])

  const { skills } = store
  const defaultRows = React.useMemo(() => {
    return skills.forOrg.flatMap(toRow)
  }, [skills])

  const fetchAllSkills = async () => {
    setAllLoading(true)
    setFailed(false)

    try {
      setData(await fetchSkillInsights())
    } catch (error) {
      setFailed(true)
      reportLoadFailure(error)
    } finally {
      setAllLoading(false)
    }
  }

  const [lastDeletedSkillId, setLastDeletedSkillId] = React.useState<
    string | null
  >(null)

  const [lastRemovedSkillId, setLastRemovedSkillId] = React.useState<
    string | null
  >(null)

  React.useEffect(() => {
    fetchAllSkills()
  }, [])

  React.useEffect(() => {
    // Don't start listening for changes until all the initial store boot has finished
    if (allLoading) return

    // Listen to changes to skills and skill variants, and refetch insight rows
    const dispose = onPatch(store, async (patch) => {
      const fetched = await processPatch(patch)
      if (!fetched) return
      setData((rows) => {
        if (!rows) return rows
        const exists = rows.some((row) => row.id === fetched.id)
        if (!exists) return [...rows, fetched]
        return rows.map((row) => (row.id === fetched.id ? fetched : row))
      })
    })

    return () => dispose()
  }, [allLoading])

  React.useEffect(() => {
    setData((rows) => {
      if (!rows) return rows
      if (lastDeletedSkillId) {
        return rows.filter((row) => row.id !== lastDeletedSkillId)
      }

      if (lastRemovedSkillId) {
        return rows.map((row) => {
          if (row.id === lastRemovedSkillId) {
            return { ...row, teamCount: 0, userCount: 0, positionCount: 0 }
          }
          return row
        })
      }
      return rows
    })
  }, [lastRemovedSkillId, lastDeletedSkillId])

  const { request } = useRailsContext()

  const defaultOpen = request.query.open_drawer ? true : false

  const addCheckedSkillId = (id: string) => {
    setCheckedSkillIds((prev) => [...prev, id])
  }

  const removeCheckedSkillId = (id: string) => {
    setCheckedSkillIds((prev) => prev.filter((skillId) => skillId !== id))
  }

  return (
    <BulkDeletedSkillContext.Provider
      value={{
        checkedSkillIds,
        addCheckedSkillId,
        removeCheckedSkillId,
      }}
    >
      <RemoveSkillContext.Provider
        value={{ lastRemovedSkillId, setLastRemovedSkillId }}
      >
        <DeletedSkillContext.Provider
          value={{ lastDeletedSkillId, setLastDeletedSkillId }}
        >
          <Nav onRefresh={() => fetchAllSkills()} defaultOpen={defaultOpen} />
          {failed && (
            <LoadFailed
              onRetry={() => fetchAllSkills()}
              what="the latest skills"
            />
          )}
          <TableProvider loading={allLoading} data={data || defaultRows}>
            <OrgSkillsTable />
          </TableProvider>
          <SkillInsightsDrawer />
        </DeletedSkillContext.Provider>
      </RemoveSkillContext.Provider>
    </BulkDeletedSkillContext.Provider>
  )
})

async function fetchSkillInsights() {
  const res = await fetch('/api/v1/skill_insights')
  if (!res.ok) throw new Error(`skill_insights fetch failed: ${res.status}`)
  const json = await res.json()
  const deserialized = deserialize(json)
  if (!deserialized?.data) return []
  const insights = deserialized.data.skillInsights as
    Record<string, OrgSkillsTableRow> | undefined
  if (!insights) return []

  return Object.values(insights).map(normalizeRow)
}

async function fetchSkillInsight(id: string) {
  let json: unknown

  try {
    json = await fetchJson(`/api/v1/skill_insights/${id}`)
  } catch (error) {
    reportLoadFailure(error)
    return
  }

  const deserialized = deserialize(json)
  if (!deserialized?.data) return
  const insights = deserialized.data.skillInsights as Record<
    string,
    OrgSkillsTableRow
  >

  return Object.values(insights).map(normalizeRow)[0]
}

function normalizeRow(row: OrgSkillsTableRow) {
  return {
    ...row,
    teamIds: row.teamIds.map((id) => id.toString()),
    variantId: row.variantId.toString(),
    first3UserIds: row.first3UserIds.map((id) => id.toString()),
    lastEdited: parseISO(`${row.lastEdited}`),
    variantNames: row.variantNames ? row.variantNames : [],
    hasIssues: row.hasIssues ? row.hasIssues : false,
  }
}

function toRow(skill: Skill): OrgSkillsTableRow | [] {
  const variant = skill.defaultVariant
  if (!variant) return []
  return {
    id: skill.id,
    name: skill.name,
    levels: 0,
    teamIds: [],
    teamCount: 0,
    userCount: 0,
    variantId: variant.id,
    editableBy: skill.editableBy,
    lastEdited: variant.updatedAt,
    description: skill.description,
    imageUrl: skill.imageUrl,
    commentCount: 0,
    first3UserIds: [],
    positionCount: 0,
    needsAttention: false,
    variantCount: skill.skillVariants.length,
    variantNames: [],
    hasIssues: false,
  }
}

async function processPatch(patch: IJsonPatch) {
  const { id, module } = patch.path.match(patchRegex)?.groups ?? {}
  if (!id || !module) return

  const skillId: string | undefined =
    module === 'skills' ? id : store.skillVariants.byId(id)?.skill.id
  if (!skillId) return

  return await fetchSkillInsight(skillId)
}
